/**
 * Bellijsten & Contacten — types + Zod-schemas voor medewerkerapp, klantenportaal
 * en backoffice. Bron: `docs/specs/bellijsten-contacten.md` §2, §3, §7, §8.
 *
 * Drie portalen consumeren deze DTOs:
 *  - Portal (medewerkerapp)  → collega's, klanten (planningsfilter), belangrijk
 *  - Klantenportaal          → geplande medewerkers + vaste klant-contacten
 *  - Backoffice              → 5 tabs (medewerkers zakelijk / privé /
 *                              noodcontact, klanten eerste contact / alle, belangrijk)
 *
 * Privé-data (privénummer + noodcontact) en "alle klantcontacten" worden
 * server-side gefilterd op rol (HR / Kasper / ADMIN). De DTOs hier maken
 * geen permissie-aannames — die zitten in de API-route (PR-B3+).
 */

import { z } from 'zod'
import { idParam } from './id-validators'
import { partialZonderDefaults } from './zod-patch'

// ─── Enums (matching Prisma `BedrijfsContactRol`) ────────────────────────────

export const BEDRIJFS_CONTACT_ROLLEN = [
  'EIGENAAR',
  'HR',
  'TEAMLEIDER',
  'VERVANGEND_TEAMLEIDER',
  'VERTROUWENSPERSOON',
  'PLANNING',
  'FACTURATIE',
  'ICT',
  'VEILIGHEID',
  'ARBO',
  'OVERIG',
] as const

export type BedrijfsContactRol = (typeof BEDRIJFS_CONTACT_ROLLEN)[number]

/**
 * Spec-alias — sommige interne docs/code refereren naar `BEDRIJFS_CONTACT_ROL`
 * (singulier). We exporteren beide voor backwards-compat met spec §8.
 */
export const BEDRIJFS_CONTACT_ROL = BEDRIJFS_CONTACT_ROLLEN

// ─── Label-records (spec §2.2) ───────────────────────────────────────────────

export const BEDRIJFS_CONTACT_ROL_LABELS: Record<BedrijfsContactRol, string> = {
  EIGENAAR:               'Eigenaar',
  HR:                     'HR',
  TEAMLEIDER:             'Teamleider',
  VERVANGEND_TEAMLEIDER:  'Vervangend teamleider',
  VERTROUWENSPERSOON:     'Vertrouwenspersoon',
  PLANNING:               'Planning',
  FACTURATIE:             'Facturatie / Werkbonnen',
  ICT:                    'ICT',
  VEILIGHEID:             'Veiligheidskundige',
  ARBO:                   'Arbodienst',
  OVERIG:                 'Overig',
}

// ─── Query-enums voor backoffice tabs (spec §3.3) ────────────────────────────

export const BELLIJST_TYPE = ['zakelijk', 'prive', 'noodcontact'] as const
export type BellijstType = (typeof BELLIJST_TYPE)[number]

export const KLANT_BELLIJST_TYPE = ['eerste-contact', 'alle-contacten'] as const
export type KlantBellijstType = (typeof KLANT_BELLIJST_TYPE)[number]

// ─── DTOs — Portal (medewerkerapp) — spec §3.1 ───────────────────────────────

/**
 * Tab 1: Collega's. Alle ACTIEF/ZIEK-medewerkers met zakelijke contactdata.
 */
export interface CollegaBellijstDTO {
  id:        string
  naam:      string                  // firstName + lastName
  functie:   string | null
  afdeling:  string | null
  telefoon:  string | null           // Employee.phone (zakelijk)
  email:     string | null           // Employee.email (zakelijk)
  fotoUrl:   string | null
}

/**
 * Tab 2: Klanten. Gegroepeerd per `Client` met geplande
 * contactpersonen — alleen klanten waar medewerker in [vandaag-1, vandaag+7]
 * gepland staat (zie spec §10 planningsfilter).
 */
export interface KlantBellijstDTO {
  clientId:   string
  klantNaam:  string
  contacten:  KlantContactDTO[]
}

/**
 * Contactpersoon binnen een klant. Wordt gedeeld door
 * `KlantBellijstDTO.contacten` (portal) en `KlantAlleContactenDTO.contacten`
 * (backoffice — uitgebreid met `afdeling` + `notities`).
 */
export interface KlantContactDTO {
  id:        string
  naam:      string
  functie:   string | null
  telefoon:  string | null
  mobiel:    string | null
  email:     string | null
}

// ─── Portal — sectie "Alle klanten" (FO-346, batch-40) ───────────────────────
//
// De klanten-tab van de portal heeft twee secties: "Mijn klussen" (het
// planningsvenster hierboven, `KlantBellijstDTO`) en "Alle klanten" — alle
// ACTIEVE klanten met minstens een contactpersoon, zoekbaar. Beide secties
// leveren dezelfde `KlantBellijstDTO`-shape; alleen de selectie verschilt.
// Een klant mag in beide secties staan (andere context, geen dedupe).

/**
 * Minimale zoeklengte voor de portal-klanten-zoek. Onder deze grens vuurt de
 * UI geen query en weigert de API 'm: anders scant elke toetsaanslag het hele
 * klantenbestand (spec §8.1). Gedeeld zodat UI-hint en server-validatie
 * gegarandeerd dezelfde grens hanteren.
 */
export const PORTAL_KLANTEN_ZOEK_MIN_LENGTE = 2

/**
 * Paginagrootte van de portal-klanten-zoek. Bewust klein: een monteur op 4G
 * krijgt geen honderden klanten in een keer, maar de eerste 25 alfabetisch met
 * een "meer laden"-knop (spec §4).
 */
export const PORTAL_KLANTEN_ZOEK_TAKE = 25

/**
 * Query voor `GET /portal/bellijst/klanten/zoek` — gedeeld door de API-
 * validatie en de portal-server-action, zodat het form nooit iets kan sturen
 * dat de API weigert.
 *
 * `zoek` weglaten = bladermodus (eerste `take` klanten alfabetisch). Een term
 * korter dan `PORTAL_KLANTEN_ZOEK_MIN_LENGTE` is een validatiefout, geen stille
 * lege lijst — dan weet de aanroeper waarom er niets komt.
 */
export const portalKlantenZoekQuerySchema = z.object({
  zoek: z
    .string()
    .trim()
    .min(
      PORTAL_KLANTEN_ZOEK_MIN_LENGTE,
      `Typ minstens ${PORTAL_KLANTEN_ZOEK_MIN_LENGTE} tekens om te zoeken`,
    )
    .max(100)
    .optional(),
  // Offset-paginering ("meer laden"). Cap voorkomt dat een handmatige call de
  // database met een absurde offset laat scannen.
  skip: z.coerce.number().int().min(0).max(5_000).optional(),
})

export type PortalKlantenZoekQuery = z.infer<typeof portalKlantenZoekQuerySchema>

/** Paginering-meta bij de portal-klanten-zoek. */
export interface PortalKlantenZoekMeta {
  /** Totaal aantal klanten dat aan de (zoek)selectie voldoet. */
  total:   number
  /** Toegepaste offset. */
  skip:    number
  /** Toegepaste paginagrootte. */
  take:    number
  /** Zijn er nog klanten na deze pagina? Stuurt de "meer laden"-knop. */
  hasMore: boolean
}

/**
 * Tab 3: Belangrijke contacten — `BedrijfsContact`-records met
 * `zichtbaarMedewerkerApp = true`. Aangevuld met de directe teamleider
 * (department-based) als eerste rij — zie spec §3.1.
 */
export interface BelangrijkContactDTO {
  id:        string
  rol:       BedrijfsContactRol
  rolLabel:  string
  naam:      string
  telefoon:  string | null
  email:     string | null
}

// ─── DTOs — Klantenportaal — spec §3.2 ───────────────────────────────────────

/**
 * Geplande medewerkers bij deze klant — zakelijke nummers only.
 * Filter: `WerkbonDayEntry.date >= today` AND status in
 * (GEPLAND, CONCEPT, INGEDIEND, GOEDGEKEURD).
 */
export interface GeplandeMedewerkerDTO {
  id:        string
  naam:      string
  functie:   string | null
  // FO-049-P3 — werk-contact is standaard verborgen; alleen gevuld na een geldige
  // vrijgave (per monteur × klant) ÉN de medewerker-opt-in (bellijst-zichtbaarheid).
  // Zonder vrijgave: null + `contactZichtbaar=false` (serverside afgedwongen).
  telefoon:  string | null           // Employee.phone (zakelijk)
  email:     string | null           // Employee.email (zakelijk)
  contactZichtbaar: boolean
}

/**
 * Belangrijke vaste contacten voor deze klant. Komt uit
 * `Client.primaryContactEmployeeId` + `contactPersoonPlanningId` +
 * `contactPersoonFacturenId` + `BedrijfsContact` (zichtbaarKlantenportaal).
 *
 * `rol` is hier een vrije label-string omdat de klantenportaal-rendering
 * 3 vaste rol-labels mengt ("Eerste contactpersoon", "Planning",
 * "Facturen/werkbonnen") met de dynamische `BEDRIJFS_CONTACT_ROL_LABELS`.
 */
export interface KlantBelangrijkContactDTO {
  rol:       string
  naam:      string
  telefoon:  string | null
  email:     string | null
}

// ─── DTOs — Backoffice — spec §3.3 ───────────────────────────────────────────

/**
 * Tab 1: Medewerkers (zakelijk). Permissie: `MEDEWERKERS.canView`.
 */
export interface MedewerkerBellijstDTO {
  id:                 string
  personeelsnummer:   string
  naam:               string
  functie:            string | null
  afdeling:           string | null
  entiteit:           string | null
  telefoon:           string | null
  email:              string | null
}

/**
 * Tab 2: Medewerkers (privé) — alleen HR/Kasper/ADMIN.
 */
export interface MedewerkerBellijstPriveDTO extends MedewerkerBellijstDTO {
  priveTelefoon:      string | null   // Employee.privatePhone
  priveEmail:         string | null   // Employee.privateEmail
}

/**
 * Tab 3: Noodcontacten — alleen HR/Kasper/ADMIN.
 */
export interface MedewerkerNoodcontactDTO {
  id:                  string
  naam:                string
  noodcontactNaam:     string | null
  noodcontactRelatie:  string | null
  noodcontactTelefoon: string | null
}

/**
 * Tab 4: Klanten (eerste contactpersonen). Permissie: `CRM.canView`.
 */
export interface KlantEersteContactDTO {
  clientId:     string
  klantNaam:    string
  contactNaam:  string
  telefoon:     string | null
  mobiel:       string | null
  email:        string | null
  functie:      string | null
}

/**
 * Tab 5: Klanten (alle contacten) — alleen Kasper.
 * Per-klant volledige contactenlijst met `afdeling` + `notities`.
 */
export interface KlantAlleContactenDTO {
  clientId:   string
  klantNaam:  string
  contacten:  Array<KlantContactDTO & {
    afdeling:  string | null
    notities:  string | null
  }>
}

/**
 * Backoffice-bellijst tab-discriminator + payload.
 *
 * De backoffice-API exposeert 5 tabs (3 medewerker-tabs, 2 klant-tabs, plus
 * "belangrijk"). Deze discriminated union maakt het mogelijk om de juiste
 * row-shape per tab te typen aan de client-zijde, zónder dat de aanroeper
 * een aparte response-type per endpoint hoeft te tracken.
 */
export type BackofficeBellijstDTO =
  | { tab: 'medewerkers';   type: 'zakelijk';        rows: MedewerkerBellijstDTO[] }
  | { tab: 'medewerkers';   type: 'prive';           rows: MedewerkerBellijstPriveDTO[] }
  | { tab: 'medewerkers';   type: 'noodcontact';     rows: MedewerkerNoodcontactDTO[] }
  | { tab: 'klanten';       type: 'eerste-contact';  rows: KlantEersteContactDTO[] }
  | { tab: 'klanten';       type: 'alle-contacten';  rows: KlantAlleContactenDTO[] }
  | { tab: 'belangrijk';                             rows: BedrijfsContactDTO[] }

// ─── DTOs — Admin CRUD voor BedrijfsContact (spec §7) ────────────────────────

/**
 * Volledig `BedrijfsContact`-record zoals geleverd door de admin-API.
 * `employeeNaam` is de gederiveerde "firstName lastName" als er een
 * employee gekoppeld is — handig voor de tabel-rendering zonder extra join
 * aan client-zijde.
 */
export interface BedrijfsContactDTO {
  id:                       string
  rol:                      BedrijfsContactRol
  label:                    string
  naam:                     string
  telefoon:                 string | null
  email:                    string | null
  employeeId:               string | null
  employeeNaam:             string | null
  zichtbaarMedewerkerApp:   boolean
  zichtbaarKlantenportaal:  boolean
  zichtbaarBackoffice:      boolean
  volgorde:                 number
  isActief:                 boolean
}

// ─── Zod-schemas — Admin CRUD (spec §7) ──────────────────────────────────────

/**
 * Create-payload voor een nieuw `BedrijfsContact`-record.
 *
 * `email` is optioneel maar áls aanwezig moet het een geldig adres zijn.
 * `telefoon` accepteert vrije tekst (geen NL-formaat-check) zodat
 * doorkiesnummers en internationale nummers ook werken.
 * `employeeId` gebruikt `idParam()` (cuid OF legacy UUID — zie MEMORY).
 */
export const createBedrijfsContactSchema = z.object({
  rol:                     z.enum(BEDRIJFS_CONTACT_ROLLEN),
  label:                   z.string().trim().min(1, 'Label is verplicht').max(200),
  naam:                    z.string().trim().min(1, 'Naam is verplicht').max(200),
  telefoon:                z.string().trim().max(50).nullable().optional(),
  email:                   z.string().trim().email('Ongeldig e-mailadres').max(200).nullable().optional(),
  employeeId:              idParam('employeeId').nullable().optional(),
  zichtbaarMedewerkerApp:  z.boolean().default(true),
  zichtbaarKlantenportaal: z.boolean().default(false),
  zichtbaarBackoffice:     z.boolean().default(true),
  volgorde:                z.number().int().min(0).default(0),
  isActief:                z.boolean().default(true),
})

export type BedrijfsContactCreateDTO = z.infer<typeof createBedrijfsContactSchema>

/**
 * Update-payload — alle velden optioneel (PATCH-semantiek).
 */
export const updateBedrijfsContactSchema = partialZonderDefaults(createBedrijfsContactSchema)

export type BedrijfsContactUpdateDTO = z.infer<typeof updateBedrijfsContactSchema>

/**
 * Reorder-payload voor drag-and-drop in de admin-UI. Server itereert
 * over de array en zet de `volgorde` per id; volgorde-waarden hoeven
 * niet uniek of contigu te zijn (gewoon: lagere `volgorde` = eerder).
 *
 * Spec §7.2: "Drag-and-drop volgorde of nummer-invoer".
 */
export const reorderBedrijfsContactSchema = z.object({
  items: z
    .array(
      z.object({
        id:       idParam(),
        volgorde: z.number().int().min(0),
      }),
    )
    .min(1, 'Minimaal 1 contact om te herordenen')
    .max(200, 'Maximaal 200 contacten per reorder-call'),
})

export type ReorderBedrijfsContactInput = z.infer<typeof reorderBedrijfsContactSchema>
